import * as React from "react";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import ModalPortalContext from "src/components/common/forms/modal/ModalPortalContext";
import { cn } from "src/lib/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-dialog bg-black/60 overflow-y-auto pointer-events-auto",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    showCloseButton?: boolean;
    fullScreen?: boolean;
    accessibleTitle?: string;
  }
>(
  (
    {
      className,
      children,
      showCloseButton = true,
      fullScreen = false,
      accessibleTitle,
      ...props
    },
    ref
  ) => {
    const [portalEl, setPortalEl] = React.useState<HTMLElement | null>(null);
    const contextValue = React.useMemo(
      () => ({ element: portalEl, register: setPortalEl }),
      [portalEl]
    );

    return (
      <DialogPortal>
        <DialogOverlay
          className={cn(
            "flex items-start justify-center",
            !fullScreen && "py-8"
          )}
        >
          <ModalPortalContext.Provider value={contextValue}>
            <DialogPrimitive.Content
              ref={ref}
              className={cn(
                "relative z-dialog-content flex flex-col w-full bg-surface-2 border border-border shadow-xl duration-200",
                "data-[state=open]:animate-in data-[state=closed]:animate-out",
                "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                fullScreen
                  ? "h-screen w-screen max-w-none m-0 rounded-none border-0"
                  : "max-w-lg rounded-xl my-auto h-fit",
                className
              )}
              aria-describedby={undefined}
              {...props}
            >
              {accessibleTitle && (
                <DialogPrimitive.Title className="sr-only">
                  {accessibleTitle}
                </DialogPrimitive.Title>
              )}
              {children}
              {showCloseButton && (
                <DialogPrimitive.Close className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground opacity-70 transition-all duration-150 hover:opacity-100 hover:bg-[hsl(var(--hover-overlay))] focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background disabled:pointer-events-none">
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
              )}
              <div
                ref={setPortalEl}
                className="fixed top-0 left-0 w-0 h-0 overflow-visible z-modal-menu-portal"
              />
            </DialogPrimitive.Content>
          </ModalPortalContext.Provider>
        </DialogOverlay>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col gap-1.5 p-6", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end gap-3 p-6 pt-4 border-t border-border",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-base font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
